import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    IconButton,
    Button,
    Typography,
    Grid2,
    FormControlLabel,
    Checkbox,
    Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { COLORS, DATE_FORMATS } from '../../../utils/Application_Constants';
import formatDate, { formatUtcDate } from '../../../utils/formatDate';
import { rateTypesEnumApiToDisplay } from '../../Configurations/processedTimesheetsConfig';
import ProcessedTimesheetLineDetailForm from './ProcessedTimesheetLineDetailForm';
import { currencyFormatter } from '../../../utils/helpers';
import useAdminAuthContext from '../../hooks/useAdminAuthContext';

function DetailItem({ label, value }) {
    return (
        <Box>
            <Typography variant="body2" color={COLORS.LT_MIDNIGHT} mb={1} sx={{ fontWeight: 600 }}>
                {label}
            </Typography>
            <Typography variant="body2" color={COLORS.LT_MIDNIGHT75}>
                {value ?? '-'}
            </Typography>
        </Box>
    );
}

const ProcessedTimesheetLineDetailModal = ({
    isOpen,
    onClose,
    data = {},
    editMode,
    addMode,
    onEditModeChange,
    processedTimesheetId,
    availableRates = [],
    weekEndingStartDate,
    weekEndingEndDate,
    isAggregatePts = false,
    locations = [],
    onDuplicate = () => {},
    refetch = () => {},
    readOnly = false,
}) => {
    const {
        date,
        rateType,
        payTypeId,
        payAmount,
        billAmount,
        payRate,
        billRate,
        payTime,
        billTime,
        billUnit,
        payUnit,
        aB5Amount,
        rebateAmount,
        discountAmount,
        processedAsHoliday,
        createdBy,
        created,
        modifiedBy,
        modified,
        locationId,
        isMileage,
    } = data || {};

    const activeLocation = locations.find((location) => location.locationId === locationId);
    const locationName = activeLocation?.locationName ?? '-';
    const { isTimesheetSystemAdminUser } = useAdminAuthContext();
    const isEntryPosted = data?.isPosted === true;
    const canEditEntry = !readOnly && !isEntryPosted && isTimesheetSystemAdminUser;

    return (
        <Dialog
            open={isOpen}
            fullWidth
            onClose={onClose}
            slotProps={{ paper: { sx: { borderRadius: '8px', padding: '24px' } } }}
        >
            <Box display="flex" alignItems="center" justifyContent="flex-start" mb={2}>
                {(editMode || addMode) && (
                    <DialogTitle sx={{ pl: 0 }}>
                        {
                            // eslint-disable-next-line no-nested-ternary
                            editMode ? 'Edit Row' : addMode ? 'Add Row' : ''
                        }
                    </DialogTitle>
                )}
                {!editMode && !addMode && !readOnly && isTimesheetSystemAdminUser && (
                    <>
                        {canEditEntry && (
                            <Button
                                color="primary"
                                onClick={() => onEditModeChange(true)}
                                endIcon={<ModeEditIcon />}
                                sx={{
                                    minWidth: 50,
                                    pl: 0,
                                    pe: 1,
                                    me: 2,
                                    '&:hover': { bgcolor: 'transparent' },
                                }}
                                disableRipple
                            >
                                Edit
                            </Button>
                        )}
                        <Button
                            color="primary"
                            onClick={() => onDuplicate(data)}
                            endIcon={<FileCopyIcon />}
                            sx={{ minWidth: 50, px: 1, '&:hover': { bgcolor: 'transparent' } }}
                            disableRipple
                        >
                            Duplicate
                        </Button>
                    </>
                )}

                <IconButton disabled={false} onClick={onClose} sx={{ marginLeft: 'auto' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {editMode || addMode ? (
                <ProcessedTimesheetLineDetailForm
                    data={data}
                    onClose={onClose}
                    availableRates={availableRates}
                    weekEndingStartDate={weekEndingStartDate}
                    weekEndingEndDate={weekEndingEndDate}
                    newEntry={addMode}
                    processedTimesheetId={processedTimesheetId}
                    refetch={refetch}
                    locations={locations}
                    isAggregatePts={isAggregatePts}
                />
            ) : (
                <DialogContent sx={{ padding: '0px' }}>
                    <Grid2 container spacing={2}>
                        <Grid2 size={6}>
                            <DetailItem label="Transaction Date" value={formatUtcDate(date)} />
                        </Grid2>
                        <Grid2 size={6}>
                            <DetailItem label="Location" value={locationName} />
                        </Grid2>
                        <Grid2 size={6}>
                            <DetailItem label="Rate Type" value={rateType} />
                        </Grid2>
                        <Grid2 size={6}>
                            <DetailItem label="Pay Type ID" value={payTypeId} />
                        </Grid2>
                        <Grid2 size={6}>
                            <DetailItem
                                label="Pay Amount"
                                value={payAmount ? currencyFormatter(payAmount) : '-'}
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <DetailItem
                                label="Bill Amount"
                                value={billAmount ? currencyFormatter(billAmount) : '-'}
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <DetailItem
                                label="Pay Rate"
                                value={
                                    payRate
                                        ? isMileage
                                            ? parseFloat(payRate)
                                            : payRate?.toFixed(2)
                                        : '-'
                                }
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <DetailItem
                                label="Bill Rate"
                                value={
                                    billRate
                                        ? isMileage
                                            ? parseFloat(billRate)
                                            : billRate?.toFixed(2)
                                        : '-'
                                }
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <DetailItem
                                label="Pay Time"
                                value={payTime ? payTime?.toFixed(2) : '-'}
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <DetailItem
                                label="Bill Time"
                                value={billTime ? billTime?.toFixed(2) : '-'}
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <DetailItem
                                label="Pay Unit"
                                value={payUnit ? rateTypesEnumApiToDisplay(payUnit) : '-'}
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <DetailItem
                                label="Bill Unit"
                                value={billUnit ? rateTypesEnumApiToDisplay(billUnit) : '-'}
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <DetailItem
                                label="AB5 Amount"
                                value={aB5Amount ? currencyFormatter(aB5Amount) : '-'}
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <DetailItem
                                label="Discount Amount"
                                value={discountAmount ? currencyFormatter(discountAmount) : '-'}
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <DetailItem
                                label="Rebate Amount"
                                value={rebateAmount ? currencyFormatter(rebateAmount) : '-'}
                            />
                        </Grid2>
                        <Grid2 size={12}>
                            <Box display="flex" alignItems="center">
                                <FormControlLabel
                                    key="processedAsHoliday"
                                    control={
                                        <Checkbox
                                            checked={processedAsHoliday}
                                            value="processedAsHoliday"
                                            color="primary"
                                            sx={{ py: 0 }}
                                        />
                                    }
                                    disabled={!editMode && !addMode}
                                    sx={{ mr: 0 }}
                                    label={
                                        <Typography
                                            variant="body2"
                                            color={COLORS.LT_MIDNIGHT}
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Processed as holiday
                                        </Typography>
                                    }
                                />
                            </Box>
                        </Grid2>
                        <Divider orientation="horizontal" flexItem style={{ width: '100%' }} />
                        <Grid2 size={6}>
                            <DetailItem
                                label={`Created${createdBy ? ' By' : ''}`}
                                value={`${createdBy ? `${createdBy}, ` : ''}${
                                    created
                                        ? `${formatDate(created, {
                                              format: DATE_FORMATS.LT_DATE_AND_TIME,
                                          })}`
                                        : '-'
                                }`}
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <DetailItem
                                label={`Modified${modifiedBy ? ' By' : ''}`}
                                value={`${modifiedBy ? `${modifiedBy}, ` : ''}${
                                    modified
                                        ? `${formatDate(modified, {
                                              format: DATE_FORMATS.LT_DATE_AND_TIME,
                                          })}`
                                        : '-'
                                }`}
                            />
                        </Grid2>
                    </Grid2>
                </DialogContent>
            )}
        </Dialog>
    );
};

export default ProcessedTimesheetLineDetailModal;
